import { Button, Row, Tooltip } from "antd";
import { usePermissions } from "../../../../../../../../../lib/hooks/usePermissions";
import { InfoCircleOutlined } from "@ant-design/icons";
import { ReactNode, useMemo } from "react";
import { IPayeeDisbursements } from "../../../../../../../../../types/PayeeDisbursements";
import { PercentageWarning } from "../../../../../../../../../components/PercentageWarning";

type HeaderProps = {
  data: IPayeeDisbursements[];
  isLoading: boolean;
  onAddClick: () => void;
};

export const Header = ({ data, isLoading, onAddClick }: HeaderProps) => {
  const { hasAccessByController } = usePermissions("ContractDisbursements");

  const activePercent: number = useMemo(() => {
    const active: IPayeeDisbursements[] = data.filter(
      (item: IPayeeDisbursements) => item.status === 1 && item.percentage,
    );

    if (active.length > 0) {
      return active.reduce(
        (sum: number, item: IPayeeDisbursements) =>
          sum + (item.percentage || 0),
        0,
      );
    }

    return -1;
  }, [data]);

  const tooltipTitle: ReactNode = useMemo(
    () => (
      <Row className="d-block">
        <p className="mb-4 mt-0 text-sm">
          Payee amounts are calculated in the order they appear in the table,
          following these rules:
        </p>
        <p className="my-0 text-sm">1. Impound / Mortgage / Passthru</p>
        <p className="my-0 text-sm">2. Fees</p>
        <p className="my-0 text-sm">3. Secondary Payee (flat amounts)</p>
        <p className="my-0 text-sm">
          4. Secondary Payee (percentages of remainder after step 3)
        </p>
        <p className="my-0 text-sm">
          5. Primary Payee / Primary Passthru (remainder)
        </p>
        <p className="mt-4 mb-0 text-sm">
          <span className="text-sm w-500">Exception:</span> For Lease/Passthru
          payment type, Fees are calculated first (before step 1).
        </p>
      </Row>
    ),
    [],
  );

  const tooltipStyle: any = useMemo(
    () => ({
      maxWidth: "363px",
    }),
    [],
  );

  return (
    <Row className="w-100 justify-space-between">
      <h5 className="h h5 ant-table-title">Payee Disbursements</h5>
      <Row className="align-center mb-6">
        <Tooltip
          placement="top"
          title={tooltipTitle}
          overlayStyle={tooltipStyle}
          overlayClassName="text-sm"
        >
          <Row className="mr-4 align-center">
            <InfoCircleOutlined className="text-md title mr-2" />
            <span className="text-md title dotted">Disbursement order</span>
          </Row>
        </Tooltip>
        {hasAccessByController("Add") && (
          <Button shape="round" onClick={onAddClick}>
            Add New
          </Button>
        )}
      </Row>
      {!isLoading && activePercent >= 0 && activePercent !== 100 && (
        <PercentageWarning value={activePercent} />
      )}
    </Row>
  );
};
